import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Users, ChevronRight, ChevronLeft, CheckCircle, Sparkles, Activity, Plus } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { createHabitForUser, fetchCoachCreatedHabitsForUser, type Habit } from '../../lib/progress';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface Respondent {
  id: string;
  userId: string;
  name: string;
  email: string;
}

const OUTCOME_OPTIONS = [
  'Build confidence',
  'Improve follow-through',
  'Reduce procrastination',
  'Create a better morning routine',
  'Improve emotional regulation',
  'Increase visibility and sales activity',
  'Build consistency',
  'Improve focus and clarity',
  'Develop resilience',
  'Other',
];

const FREQUENCY_OPTIONS: { value: 'daily' | 'weekly'; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const STEP_LABELS = [
  'Client',
  'Outcome',
  'Pattern',
  'Identity',
  'Design',
  'Trigger',
  'Obstacles',
  'Reward',
  'Review',
];

interface WizardForm {
  respondentUserId: string;
  respondentName: string;
  outcome: string;
  outcomeCustom: string;
  linked_pattern: string;
  identity_shift: string;
  title: string;
  description: string;
  minimum_version: string;
  ideal_version: string;
  frequency: 'daily' | 'weekly';
  target_days_per_week: number;
  trigger_cue: string;
  obstacle_plan: string;
  reward: string;
}

const EMPTY_FORM: WizardForm = {
  respondentUserId: '',
  respondentName: '',
  outcome: '',
  outcomeCustom: '',
  linked_pattern: '',
  identity_shift: '',
  title: '',
  description: '',
  minimum_version: '',
  ideal_version: '',
  frequency: 'daily',
  target_days_per_week: 5,
  trigger_cue: '',
  obstacle_plan: '',
  reward: '',
};

function StepIndicator({ currentStep, total }: { currentStep: number; total: number }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
            i < currentStep ? 'bg-sage-500 text-white' :
            i === currentStep ? 'bg-sage-600 text-white ring-2 ring-sage-200' :
            'bg-cream-200 text-chocolate-400'
          }`}>
            {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
          </div>
          {i < total - 1 && (
            <div className={`h-0.5 w-4 ${i < currentStep ? 'bg-sage-400' : 'bg-cream-200'}`} />
          )}
        </div>
      ))}
      <span className="ml-3 text-sm text-chocolate-500 font-medium">{STEP_LABELS[currentStep]}</span>
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="freud-label flex items-center gap-1">
      {children}
      {required && <span className="text-coral-500">*</span>}
    </label>
  );
}

export function HabitInstallationOS() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdHabit, setCreatedHabit] = useState<Habit | null>(null);

  // Plans history
  const [selectedPlanUserId, setSelectedPlanUserId] = useState<string | null>(null);
  const [plans, setPlans] = useState<Habit[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) { navigate('/'); return; }

      const { data: coach } = await supabase
        .from('coaches')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!coach) { navigate('/login'); return; }
      setCoachId(coach.id);

      const { data: respondentsData } = await supabase
        .from('respondents')
        .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
        .eq('coach_id', coach.id);

      setRespondents((respondentsData || []).map(r => ({
        id: r.id,
        userId: r.user_id,
        name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
        email: (r.users as any)?.email || '',
      })));

      setIsLoading(false);
    };

    init();
  }, [user, authLoading, navigate]);

  const set = (key: keyof WizardForm, value: string | number) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const canAdvance = (): boolean => {
    if (step === 0) return !!form.respondentUserId;
    if (step === 1) return !!(form.outcome === 'Other' ? form.outcomeCustom.trim() : form.outcome);
    if (step === 4) return !!(form.title.trim());
    return true;
  };

  const handleNext = () => {
    if (!canAdvance()) return;
    setStep(s => s + 1);
  };

  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!coachId || !form.respondentUserId) return;
    setSubmitting(true);
    try {
      const outcome = form.outcome === 'Other' ? form.outcomeCustom : form.outcome;
      const created = await createHabitForUser(form.respondentUserId, coachId, {
        title: form.title,
        description: form.description,
        frequency: form.frequency,
        target_days_per_week: form.target_days_per_week,
        outcome,
        linked_pattern: form.linked_pattern,
        identity_shift: form.identity_shift,
        minimum_version: form.minimum_version,
        ideal_version: form.ideal_version,
        trigger_cue: form.trigger_cue,
        obstacle_plan: form.obstacle_plan,
        reward: form.reward,
      });
      if (created) {
        setCreatedHabit(created);
        setSubmitted(true);
        setStep(9);
      }
    } catch (err) {
      console.error('Error creating habit plan:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setStep(0);
    setSubmitted(false);
    setCreatedHabit(null);
  };

  const loadPlans = async (userId: string) => {
    setPlansLoading(true);
    setSelectedPlanUserId(userId);
    const data = await fetchCoachCreatedHabitsForUser(userId);
    setPlans(data);
    setPlansLoading(false);
  };

  if (authLoading || isLoading) return <LoadingSpinner message="Loading..." />;

  const effectiveOutcome = form.outcome === 'Other' ? form.outcomeCustom : form.outcome;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-coral-500 rounded-xl shadow-soft">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">Habit Installation OS™</h1>
              <p className="freud-text-sm text-chocolate-500">Design and install transformational habits for your clients</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Left: Client Plans History */}
          <div className="w-64 flex-shrink-0">
            <div className="freud-card-elevated overflow-hidden">
              <div className="px-4 py-3 border-b border-cream-200 flex items-center gap-2">
                <Users className="w-4 h-4 text-chocolate-500" />
                <span className="text-sm font-semibold text-chocolate-700">Client Plans</span>
              </div>
              <div className="divide-y divide-cream-100 max-h-[calc(100vh-280px)] overflow-y-auto">
                {respondents.length === 0 ? (
                  <p className="p-4 text-xs text-chocolate-400">No clients yet</p>
                ) : (
                  respondents.map(r => (
                    <button
                      key={r.id}
                      onClick={() => loadPlans(r.userId)}
                      className={`w-full text-left px-4 py-3 transition-colors hover:bg-cream-50 ${selectedPlanUserId === r.userId ? 'bg-coral-50 border-l-2 border-coral-500' : ''}`}
                    >
                      <p className="text-sm font-medium text-chocolate-900 truncate">{r.name}</p>
                      <p className="text-xs text-chocolate-400 truncate">{r.email}</p>
                    </button>
                  ))
                )}
              </div>
            </div>

            {selectedPlanUserId && (
              <div className="mt-4 freud-card-elevated overflow-hidden">
                <div className="px-4 py-3 border-b border-cream-200">
                  <span className="text-sm font-semibold text-chocolate-700">Habit Plans</span>
                </div>
                {plansLoading ? (
                  <div className="p-4 text-center text-xs text-chocolate-400">Loading...</div>
                ) : plans.length === 0 ? (
                  <p className="p-4 text-xs text-chocolate-400">No plans created yet</p>
                ) : (
                  <div className="divide-y divide-cream-100 max-h-64 overflow-y-auto">
                    {plans.map(p => {
                      const thisWeek = (p.completions || []).filter(c => {
                        const d = new Date(c.completed_date);
                        const now = new Date();
                        const weekStart = new Date(now);
                        weekStart.setDate(now.getDate() - now.getDay());
                        return d >= weekStart;
                      }).length;
                      return (
                        <div key={p.id} className="px-4 py-3">
                          <p className="text-sm font-medium text-chocolate-900 truncate">{p.title}</p>
                          {p.outcome && <p className="text-xs text-chocolate-400 truncate">{p.outcome}</p>}
                          <p className="text-xs text-sage-600 mt-1">{thisWeek}/{p.target_days_per_week} this week</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Wizard */}
          <div className="flex-1 max-w-2xl">
            {/* Success screen */}
            {step === 9 && submitted && createdHabit ? (
              <div className="freud-card-elevated p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-sage-600" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900 mb-2">Habit Plan Created!</h2>
                  <p className="text-chocolate-500">The habit has been sent to <strong>{form.respondentName}</strong>'s tracker.</p>
                </div>

                {/* Summary card */}
                <div className="bg-sage-50 border border-sage-200 rounded-2xl p-6 space-y-3 mb-8">
                  <h3 className="font-semibold text-chocolate-900 text-lg">{createdHabit.title}</h3>
                  {effectiveOutcome && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Purpose</span>
                      <span className="text-sm text-chocolate-700">{effectiveOutcome}</span>
                    </div>
                  )}
                  {form.linked_pattern && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Linked Pattern</span>
                      <span className="text-sm text-chocolate-700">{form.linked_pattern}</span>
                    </div>
                  )}
                  {form.identity_shift && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Identity Shift</span>
                      <span className="text-sm text-chocolate-700 italic">"{form.identity_shift}"</span>
                    </div>
                  )}
                  {form.minimum_version && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Minimum</span>
                      <span className="text-sm text-chocolate-700">{form.minimum_version}</span>
                    </div>
                  )}
                  {form.ideal_version && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Ideal</span>
                      <span className="text-sm text-chocolate-700">{form.ideal_version}</span>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Frequency</span>
                    <span className="text-sm text-chocolate-700">{form.target_days_per_week} days/week ({form.frequency})</span>
                  </div>
                  {form.trigger_cue && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Trigger</span>
                      <span className="text-sm text-chocolate-700">{form.trigger_cue}</span>
                    </div>
                  )}
                  {form.obstacle_plan && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">If Stuck</span>
                      <span className="text-sm text-chocolate-700">{form.obstacle_plan}</span>
                    </div>
                  )}
                  {form.reward && (
                    <div className="flex gap-3">
                      <span className="text-xs font-semibold text-chocolate-400 uppercase w-32 flex-shrink-0">Reward</span>
                      <span className="text-sm text-chocolate-700">{form.reward}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button onClick={handleReset} className="freud-btn-primary flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Create Another
                  </button>
                  <button onClick={() => loadPlans(form.respondentUserId)} className="freud-btn-secondary">
                    View All Plans
                  </button>
                </div>
              </div>
            ) : (
              <div className="freud-card-elevated p-8">
                <StepIndicator currentStep={step} total={9} />

                {/* Step 0: Select client */}
                {step === 0 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Select a Client</h2>
                    <p className="text-chocolate-500 mb-6">Which client are you designing this habit plan for?</p>
                    {respondents.length === 0 ? (
                      <div className="p-8 text-center text-chocolate-400">
                        <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>No clients found. Add respondents first.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {respondents.map(r => (
                          <button
                            key={r.id}
                            onClick={() => { set('respondentUserId', r.userId); set('respondentName', r.name); }}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left ${
                              form.respondentUserId === r.userId
                                ? 'border-sage-500 bg-sage-50'
                                : 'border-cream-200 hover:border-cream-400'
                            }`}
                          >
                            <div>
                              <p className="font-medium text-chocolate-900">{r.name}</p>
                              <p className="text-xs text-chocolate-400">{r.email}</p>
                            </div>
                            {form.respondentUserId === r.userId && <CheckCircle className="w-5 h-5 text-sage-500" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 1: Outcome */}
                {step === 1 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Choose the Outcome</h2>
                    <p className="text-chocolate-500 mb-6">What change is this habit designed to create for <strong>{form.respondentName}</strong>?</p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {OUTCOME_OPTIONS.map(o => (
                        <button
                          key={o}
                          onClick={() => set('outcome', o)}
                          className={`px-4 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                            form.outcome === o
                              ? 'border-sage-500 bg-sage-50 text-sage-700 font-medium'
                              : 'border-cream-200 text-chocolate-700 hover:border-cream-400'
                          }`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                    {form.outcome === 'Other' && (
                      <div>
                        <FieldLabel required>Describe the outcome</FieldLabel>
                        <input className="freud-input" value={form.outcomeCustom} onChange={e => set('outcomeCustom', e.target.value)} placeholder="e.g. Build daily writing habit" />
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Linked pattern */}
                {step === 2 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Link to Pattern</h2>
                    <p className="text-chocolate-500 mb-6">Which pattern from {form.respondentName}'s profile does this habit address?</p>
                    <div>
                      <FieldLabel>Pattern or behaviour this habit supports</FieldLabel>
                      <textarea
                        className="freud-input resize-none"
                        rows={3}
                        value={form.linked_pattern}
                        onChange={e => set('linked_pattern', e.target.value)}
                        placeholder="e.g. Low follow-through, avoidance under pressure, fear of visibility"
                      />
                      <p className="text-xs text-chocolate-400 mt-1">Optional — connects the habit to their assessment profile</p>
                    </div>
                  </div>
                )}

                {/* Step 3: Identity shift */}
                {step === 3 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Define the Identity Shift</h2>
                    <p className="text-chocolate-500 mb-6">Who is {form.respondentName} becoming through this habit?</p>
                    <div>
                      <FieldLabel>Identity statement</FieldLabel>
                      <textarea
                        className="freud-input resize-none"
                        rows={3}
                        value={form.identity_shift}
                        onChange={e => set('identity_shift', e.target.value)}
                        placeholder="e.g. I am becoming someone who keeps small promises to themselves"
                      />
                      <p className="text-xs text-chocolate-400 mt-2">Optional — gives the habit deeper meaning and motivation</p>
                    </div>
                  </div>
                )}

                {/* Step 4: Design the habit */}
                {step === 4 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Design the Habit</h2>
                    <p className="text-chocolate-500 mb-6">Define exactly what {form.respondentName} will do.</p>
                    <div className="space-y-4">
                      <div>
                        <FieldLabel required>Habit name</FieldLabel>
                        <input className="freud-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Daily outreach" required />
                      </div>
                      <div>
                        <FieldLabel>Description</FieldLabel>
                        <input className="freud-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional details" />
                      </div>
                      <div>
                        <FieldLabel>Minimum version</FieldLabel>
                        <input className="freud-input" value={form.minimum_version} onChange={e => set('minimum_version', e.target.value)} placeholder="e.g. Send 1 message (when energy is low)" />
                        <p className="text-xs text-chocolate-400 mt-1">The version they do even on hard days</p>
                      </div>
                      <div>
                        <FieldLabel>Ideal version</FieldLabel>
                        <input className="freud-input" value={form.ideal_version} onChange={e => set('ideal_version', e.target.value)} placeholder="e.g. Send 10 messages (when fully motivated)" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <FieldLabel>Frequency</FieldLabel>
                          <select className="freud-input" value={form.frequency} onChange={e => set('frequency', e.target.value as 'daily' | 'weekly')}>
                            {FREQUENCY_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <FieldLabel>Target days / week</FieldLabel>
                          <input type="number" min={1} max={7} className="freud-input" value={form.target_days_per_week} onChange={e => set('target_days_per_week', Number(e.target.value))} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Trigger / cue */}
                {step === 5 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Set the Trigger</h2>
                    <p className="text-chocolate-500 mb-6">When and where will {form.respondentName} do this habit?</p>
                    <div>
                      <FieldLabel>Trigger / cue</FieldLabel>
                      <textarea
                        className="freud-input resize-none"
                        rows={3}
                        value={form.trigger_cue}
                        onChange={e => set('trigger_cue', e.target.value)}
                        placeholder="e.g. After morning coffee, before opening email at 9:30 AM"
                      />
                      <p className="text-xs text-chocolate-400 mt-1">A specific time, place, or existing routine that triggers the habit</p>
                    </div>
                  </div>
                )}

                {/* Step 6: Obstacle plan */}
                {step === 6 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Remove Friction</h2>
                    <p className="text-chocolate-500 mb-6">What could stop this habit, and how will {form.respondentName} handle it?</p>
                    <div>
                      <FieldLabel>Obstacle and response plan</FieldLabel>
                      <textarea
                        className="freud-input resize-none"
                        rows={4}
                        value={form.obstacle_plan}
                        onChange={e => set('obstacle_plan', e.target.value)}
                        placeholder="e.g. If I feel tired, I do the minimum version. If I miss a day, I restart the next day without guilt."
                      />
                      <p className="text-xs text-chocolate-400 mt-1">If / then scenarios that keep the habit going when motivation drops</p>
                    </div>
                  </div>
                )}

                {/* Step 7: Reward */}
                {step === 7 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Set the Reward</h2>
                    <p className="text-chocolate-500 mb-6">How will {form.respondentName} acknowledge and reinforce completion?</p>
                    <div>
                      <FieldLabel>Reward / reinforcement</FieldLabel>
                      <textarea
                        className="freud-input resize-none"
                        rows={3}
                        value={form.reward}
                        onChange={e => set('reward', e.target.value)}
                        placeholder="e.g. Tick the tracker, short journal reflection, weekly review with coach"
                      />
                    </div>
                  </div>
                )}

                {/* Step 8: Review */}
                {step === 8 && (
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900 mb-1">Review & Send</h2>
                    <p className="text-chocolate-500 mb-6">Review the habit plan before sending it to {form.respondentName}'s tracker.</p>
                    <div className="bg-cream-50 border border-cream-200 rounded-2xl p-5 space-y-2 mb-6 text-sm">
                      <SummaryRow label="Client" value={form.respondentName} />
                      <SummaryRow label="Habit" value={form.title} highlight />
                      {effectiveOutcome && <SummaryRow label="Purpose" value={effectiveOutcome} />}
                      {form.linked_pattern && <SummaryRow label="Linked Pattern" value={form.linked_pattern} />}
                      {form.identity_shift && <SummaryRow label="Identity Shift" value={`"${form.identity_shift}"`} italic />}
                      {form.minimum_version && <SummaryRow label="Minimum" value={form.minimum_version} />}
                      {form.ideal_version && <SummaryRow label="Ideal" value={form.ideal_version} />}
                      <SummaryRow label="Frequency" value={`${form.target_days_per_week} days/week (${form.frequency})`} />
                      {form.trigger_cue && <SummaryRow label="Trigger" value={form.trigger_cue} />}
                      {form.obstacle_plan && <SummaryRow label="If Stuck" value={form.obstacle_plan} />}
                      {form.reward && <SummaryRow label="Reward" value={form.reward} />}
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-cream-200">
                  <button
                    onClick={handleBack}
                    disabled={step === 0}
                    className="freud-btn-secondary flex items-center gap-2 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>

                  {step < 8 ? (
                    <button
                      onClick={handleNext}
                      disabled={!canAdvance()}
                      className="freud-btn-primary flex items-center gap-2 disabled:opacity-40"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="freud-btn-primary flex items-center gap-2"
                    >
                      {submitting ? 'Creating...' : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Send to Client Tracker
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function SummaryRow({ label, value, highlight, italic }: { label: string; value: string; highlight?: boolean; italic?: boolean }) {
  return (
    <div className="flex gap-3">
      <span className="text-xs font-semibold text-chocolate-400 uppercase w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-chocolate-700 ${highlight ? 'font-semibold' : ''} ${italic ? 'italic' : ''}`}>{value}</span>
    </div>
  );
}
